/**
 * `<DependencyArrows/>` — the dependency layer drawn OVER the tidy tree
 * (B2.7 + the B2.1 "governance before data" treatment).
 *
 * Issue #766 (Phase 4). This is the SECOND, distinct layer §A5 describes:
 * the tree's solid orthogonal connectors (drawn by `<TaskCanvas/>`) carry
 * `parent_id` decomposition; THESE dashed teal arrows carry the
 * `task_dependencies` DAG, which may cross branches. Keeping them in a
 * separate SVG with a visibly different stroke (dashed teal vs solid mint)
 * is exactly the "decomposition vs dependency, kept apart" the design wants —
 * the rendered knowledge-graph view, not a hairball.
 *
 * Issue #929 (frontend). The edges become INTERACTIVE: each visible dashed
 * arrow gets a wide, transparent hit target beneath it so the thin line is
 * comfortably clickable. Clicking an edge SELECTS it (the editor opens an
 * edge popover anchored at the edge midpoint); the selected edge is
 * highlighted (thicker, full-opacity teal). An edge that carries a free-text
 * `note` shows that note as a small calm label on the edge, so the annotation
 * is visible WITHOUT selecting. The layer stays gated `task_dependencies`:
 * when off, the canvas never mounts it, so this whole interactive layer is
 * absent (the editor is byte-unchanged).
 *
 * Issue #1043 (link-feature regression fix — the visual/interactive SPLIT).
 * #1079 raised the dependency layer ABOVE the resting node cards (z-5 vs z-1)
 * so a crossing edge reads clearly over them — a win we KEEP. But the per-edge
 * interactive `<g>` opted into `pointer-events: auto`, so the wide transparent
 * hit-stroke sat above the cards too and STOLE the pointer wherever an edge
 * crossed a card — breaking focus-click, link-port arm, and drag-to-link DROP
 * on any card under an edge (the founder's "link feature stopped responding").
 *
 * The fix separates the VISUAL from the HIT-AREA via the `layer` prop:
 *   - `"visual"`   — halo + dashed stroke + arrowhead + note label. ALWAYS
 *                    `pointer-events: none`, rendered ABOVE the cards (z-5), so
 *                    edges stay visible over/around cards (the #1079 win).
 *   - `"interactive"` — ONLY the wide transparent click-strokes that open the
 *                    edge popover. Rendered BELOW the cards (no z-index, mounted
 *                    before them) so a card always wins the pointer where an
 *                    edge merely crosses it, yet edges stay selectable in the
 *                    open space between cards.
 * The two layers share the SAME pure geometry helpers (`anchorPoints`,
 * `arrowCubic`, `obstaclesFor`) so the visible arc and its hit-stroke trace the
 * identical path. The read-only temporal views pass no select handler, so the
 * interactive layer draws nothing (the inert layer is byte-unchanged).
 *
 * Treatment lifted verbatim from the gallery SVGs so the built surface
 * matches:
 *   - stroke `#0D9488`, width 1.5, `stroke-dasharray="5 3"` (B2-1);
 *   - an arrowhead marker (`dep-arrow`) filled `#0D9488`, pointing at the
 *     LATER task (`to`);
 *   - a gentle cubic curve between node edges so crossing arcs read as arcs,
 *     not collisions.
 *
 * Pure + presentational: it takes resolved node positions (from `layoutTree`)
 * + the edge list and draws. No data fetching, no graph maths beyond the
 * geometry (the DAG maths lives in `tree/dependencyGraph.ts`).
 */

import { useId } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import type { PositionedNode } from "../tree/layout";
import { PALETTE } from "../tree/palette";

/**
 * Legacy teal, matching the gallery dependency-arrow markers (`dep21` /
 * `dep27`). The editorial theme swaps this for the warm `--teal` (#3F8B8B);
 * the resolved colour is read from the theme inside the component.
 */
const DEP_COLOR = "#0D9488";

/** A dependency edge resolved to its two endpoint node ids (+ optional note). */
export interface DependencyArrowEdge {
  id: string;
  from_node_id: string;
  to_node_id: string;
  /** Free-text "what kind of link" label (#929), or null/absent when unset. */
  note?: string | null;
}

export interface DependencyArrowsProps {
  /** All positioned nodes (from `layoutTree`), keyed lookup for endpoints. */
  positions: readonly PositionedNode[];
  /** The dependency edges to draw (`from` COMES BEFORE `to`). */
  edges: readonly DependencyArrowEdge[];
  /** Offset applied to the tree content (the canvas's CANVAS_PAD). */
  offset: number;
  /** Total SVG width/height (the canvas content plane size). */
  width: number;
  height: number;
  /**
   * The currently selected edge id (#929), or null. The selected edge is
   * highlighted; only meaningful when the layer is interactive. Omit for the
   * read-only temporal views (no selection there).
   */
  selectedEdgeId?: string | null;
  /**
   * Select an edge by id (#929) — fired by a click on the edge's hit target.
   * Omit to render the arrows non-interactive (the read-only temporal views):
   * with no handler, the hit targets and pointer affordances are not drawn and
   * the SVG keeps its old `pointer-events-none` inertness.
   */
  onSelectEdge?: (id: string) => void;
  /**
   * Which half of the split (#1043) this instance renders:
   *   - `"visual"` (default) — halo + dashed stroke + arrowhead + note label,
   *     mounted ABOVE the cards, always pointer-through.
   *   - `"interactive"` — ONLY the wide transparent click-strokes, mounted
   *     BELOW the cards so a card wins the pointer where an edge crosses it.
   * The canvas mounts BOTH (visual above, interactive below) for the same edge
   * set; the read-only views mount only the visual layer (no `onSelectEdge`).
   */
  layer?: "visual" | "interactive";
  /**
   * Every node id in the live tree (archived excluded). Round-4 ⑤.d + Bug A
   * (#1043). Used to split a "missing endpoint" edge into two cases:
   *   - the missing endpoint IS in this set ⇒ COLLAPSED (an ancestor folded the
   *     subtree); a calm "linked task hidden" cue lets the user expand to see;
   *   - the missing endpoint is NOT in this set ⇒ ARCHIVED (the link partner
   *     was removed); a calm "linked task removed" cue lets the lead restore.
   * When omitted, every dropped edge falls back to the historic silent skip —
   * existing callers + the read-only temporal views are byte-unchanged.
   */
  liveNodeIds?: ReadonlySet<string>;
  /**
   * The forest of live nodes (archived excluded). Round-4 Bug A. The cue for a
   * collapsed endpoint needs to walk the missing endpoint's parent chain to
   * find the COLLAPSED ANCESTOR id (so a click can un-collapse it). When
   * omitted, the collapsed-endpoint cue still renders informational (no expand
   * action). The shape is a minimal `{id, parentId, children?}` adjacency keyed
   * by id — supplied as a `Map` so the lookup stays O(1).
   */
  liveForestById?: ReadonlyMap<string, { id: string; parentId: string | null }>;
  /**
   * The currently-collapsed node id set. Round-4 Bug A. Used together with
   * `liveForestById` to resolve the collapsed ancestor for a hidden endpoint.
   * When omitted, the collapsed cue is informational (no expand action).
   */
  collapsedIds?: ReadonlySet<string>;
  /**
   * Restore an archived dep-endpoint by id (round-4 ⑤.d). Wired by the lead
   * editor to `useRestoreTaskNode`; after success the cache refetches and the
   * edge reappears normally. Omit on the member view (members can't restore):
   * the cue then stays informational, no Restore affordance painted.
   */
  onRestoreArchivedEndpoint?: (archivedNodeId: string) => void;
  /**
   * Un-collapse an ancestor so a hidden endpoint becomes visible again
   * (round-4 Bug A). Wired by the lead editor to `useTreeUiStore.expand`.
   * Omit to keep the cue informational (no expand affordance).
   */
  onExpandToReveal?: (collapsedAncestorId: string) => void;
}

/**
 * Resolve where an arrow should leave / enter a node. We attach to the
 * node's vertical mid-line on the side facing the other node, so an arrow
 * between two cards on the same row leaves the right edge of the earlier and
 * enters the left edge of the later (the B2.7 layout); arrows between rows
 * curve naturally.
 */
function anchorPoints(from: PositionedNode, to: PositionedNode) {
  const fromMidY = from.y + from.height / 2;
  const toMidY = to.y + to.height / 2;
  const goingRight = to.cx >= from.cx;
  return {
    x1: goingRight ? from.x + from.width : from.x,
    y1: fromMidY,
    x2: goingRight ? to.x : to.x + to.width,
    y2: toMidY,
  };
}

/** A node box an edge may need to route around (its endpoints excluded). */
interface Obstacle {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Clearance (px) kept between a routed edge's bow and an obstructing card. */
const OBSTACLE_BOW_CLEARANCE = 28;

/**
 * Does the straight chord from (x1,y1)→(x2,y2) pass close to / through `box`?
 * A cheap proximity test: sample a few points along the chord and check if any
 * lands inside the box inflated by the clearance. Good enough to decide "this
 * edge would visually disappear behind that card" without a full segment-rect
 * intersection — we only need to know whether to BOW, not the exact hit point.
 */
function chordHitsBox(x1: number, y1: number, x2: number, y2: number, box: Obstacle): boolean {
  const left = box.x - OBSTACLE_BOW_CLEARANCE;
  const right = box.x + box.width + OBSTACLE_BOW_CLEARANCE;
  const top = box.y - OBSTACLE_BOW_CLEARANCE;
  const bottom = box.y + box.height + OBSTACLE_BOW_CLEARANCE;
  const SAMPLES = 12;
  for (let i = 1; i < SAMPLES; i++) {
    const t = i / SAMPLES;
    const px = x1 + (x2 - x1) * t;
    const py = y1 + (y2 - y1) * t;
    if (px >= left && px <= right && py >= top && py <= bottom) return true;
  }
  return false;
}

/**
 * The control points of the gentle cubic between two anchor points. Horizontal
 * pull makes a same-row arrow bow softly (the B2-7 curve) and a cross-row arrow
 * read as an arc rather than a straight line cutting through cards. The cubic
 * is `(x1,y1) → (cx1,cy1) → (cx2,cy2) → (x2,y2)`; both the path string and the
 * t=0.5 midpoint derive from these, so the curve has one definition.
 *
 * Obstacle-aware routing (#1043, Problem A): when an intervening node card sits
 * on the straight chord, the edge would pass BEHIND it and vanish. We detect
 * the obstacle(s) the chord crosses and push BOTH control points perpendicular
 * to the chord, away from the obstacles' centre-of-mass, by enough to clear the
 * tallest intrusion. The result visibly bows AROUND the card rather than
 * through it. This is a pragmatic single-arc deflection, not a graph router —
 * it makes "the link curves around the box", which is all the design asks.
 */
function arrowCubic(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  obstacles: readonly Obstacle[] = [],
) {
  const pull = Math.max(40, Math.abs(x2 - x1) * 0.4);
  const dir = x2 >= x1 ? 1 : -1;
  let cx1 = x1 + dir * pull;
  let cy1 = y1;
  let cx2 = x2 - dir * pull;
  let cy2 = y2;

  // Which obstacles does the straight chord cross? (Endpoints are excluded by
  // the caller.) If none, the cubic is the plain gentle bow — byte-identical to
  // the pre-#1043 curve, so the existing arrow geometry is unchanged.
  const crossed = obstacles.filter((o) => chordHitsBox(x1, y1, x2, y2, o));
  if (crossed.length > 0) {
    // The chord's unit perpendicular (rotate the chord direction by 90°).
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    // Push away from the obstacles' centre-of-mass: pick the perpendicular sign
    // that increases distance from that centre, so the bow leaves the cards on
    // the OTHER side of the curve.
    const chordMidX = (x1 + x2) / 2;
    const chordMidY = (y1 + y2) / 2;
    const obsCx = crossed.reduce((s, o) => s + o.x + o.width / 2, 0) / crossed.length;
    const obsCy = crossed.reduce((s, o) => s + o.y + o.height / 2, 0) / crossed.length;
    const sign = (chordMidX - obsCx) * nx + (chordMidY - obsCy) * ny >= 0 ? 1 : -1;
    // Offset = half the obstacle's extent across the perpendicular + clearance,
    // so the control polygon (and thus the bowed curve) clears the card. The
    // cubic at t=0.5 reaches ~3/4 of the control offset, so we over-provision.
    const maxExtent = crossed.reduce((m, o) => Math.max(m, Math.max(o.width, o.height)), 0);
    const offset = sign * (maxExtent / 2 + OBSTACLE_BOW_CLEARANCE * 2);
    cx1 += nx * offset;
    cy1 += ny * offset;
    cx2 += nx * offset;
    cy2 += ny * offset;
  }
  return { x1, y1, x2, y2, cx1, cy1, cx2, cy2 };
}

/** The SVG path `d` for the cubic. */
function arrowPathFromCubic(c: ReturnType<typeof arrowCubic>): string {
  return `M ${c.x1} ${c.y1} C ${c.cx1} ${c.cy1}, ${c.cx2} ${c.cy2}, ${c.x2} ${c.y2}`;
}

/** The cubic's t=0.5 point: `(P0 + 3·P1 + 3·P2 + P3) / 8` — the visual middle of the bowed arc. */
function arrowMidFromCubic(c: ReturnType<typeof arrowCubic>): { x: number; y: number } {
  return {
    x: (c.x1 + 3 * c.cx1 + 3 * c.cx2 + c.x2) / 8,
    y: (c.y1 + 3 * c.cy1 + 3 * c.cy2 + c.y2) / 8,
  };
}

/**
 * The obstacle boxes an edge from `fromId`→`toId` might route around: every
 * laid-out node EXCEPT the two endpoints. (The endpoints are where the edge
 * starts/ends, so the arrow naturally touches them — only INTERVENING cards are
 * obstacles.) Kept tiny + pure so both the render and `edgeMidpoint` route
 * identically.
 */
function obstaclesFor(
  positions: readonly PositionedNode[],
  fromId: string,
  toId: string,
): Obstacle[] {
  return positions
    .filter((p) => p.node.id !== fromId && p.node.id !== toId)
    .map((p) => ({ x: p.x, y: p.y, width: p.width, height: p.height }));
}

/**
 * The midpoint of an edge's cubic, in CONTENT-PLANE coordinates (i.e. already
 * offset by `offset`). Used both for the on-edge note label AND so the canvas
 * can anchor the edge popover (#929) at the same point. Returns null when an
 * endpoint isn't currently laid out (a collapsed subtree). The point is the
 * cubic's t=0.5 (the visual middle of the bowed arc), not the chord midpoint,
 * so the label/anchor sits ON the curve.
 */
export function edgeMidpoint(
  positions: readonly PositionedNode[],
  edge: DependencyArrowEdge,
  offset: number,
): { x: number; y: number } | null {
  const byId = new Map(positions.map((p) => [p.node.id, p]));
  const from = byId.get(edge.from_node_id);
  const to = byId.get(edge.to_node_id);
  if (!from || !to) return null;
  const { x1, y1, x2, y2 } = anchorPoints(from, to);
  // Route around the same obstacles the rendered arrow does, so the popover
  // anchor sits on the (possibly bowed) curve, not the straight chord.
  const obstacles = obstaclesFor(positions, edge.from_node_id, edge.to_node_id);
  const mid = arrowMidFromCubic(arrowCubic(x1, y1, x2, y2, obstacles));
  return { x: mid.x + offset, y: mid.y + offset };
}

/**
 * The data a `<BrokenLinkCue/>` needs to paint one calm cue on the surviving
 * endpoint of a dropped edge (round-4 ⑤.d + Bug A).
 */
interface BrokenLinkCueInfo {
  /** The dependency edge this cue stands in for. */
  edgeId: string;
  /** The endpoint that IS laid out — the cue anchors next to this card. */
  surviving: PositionedNode;
  /** The endpoint that is NOT laid out (archived OR hidden by collapse). */
  missingId: string;
  /**
   * Which side of the surviving card the cue sits on. The missing endpoint was
   * conceptually "on this side" of the edge — `left` = the missing endpoint was
   * the `from`, `right` = it was the `to`.
   */
  side: "left" | "right";
  /** Trimmed edge note (may be empty); rendered as a one-liner under the chip. */
  note: string;
}

/**
 * Walk up the parent chain in the live forest until a node in `collapsedIds`
 * is found — that ancestor is what folded the missing endpoint out of view, so
 * un-collapsing it is what brings the link back. Returns the ancestor id or
 * null when no ancestor is collapsed (e.g. the forest was rebuilt mid-render).
 * Pure, O(depth).
 */
function findCollapsedAncestor(
  startNodeId: string,
  liveForestById: ReadonlyMap<string, { id: string; parentId: string | null }>,
  collapsedIds: ReadonlySet<string>,
): string | null {
  let cursor: string | null = startNodeId;
  // Guard against a corrupted parent chain (cycles) — depth caps at 64, well
  // above any realistic WBS depth, so a malformed forest can't hang the render.
  for (let i = 0; i < 64 && cursor !== null; i++) {
    if (collapsedIds.has(cursor)) return cursor;
    const node = liveForestById.get(cursor);
    if (!node) return null;
    cursor = node.parentId;
  }
  return null;
}

/**
 * `<BrokenLinkCue/>` — the calm chip painted on a surviving endpoint when its
 * dep partner is hidden (archived or collapsed). Brand firewall (round-4
 * ⑤.d): about the WORK ("linked task removed/hidden"), never alarm-red, the
 * factual edge note rides along, and the Restore/Expand affordance is a calm
 * verb (never "Recover from error" / "Fix this"). Mounted INSIDE the same
 * `<g transform>` as the arrows so the cue pans/zooms with the canvas.
 */
function BrokenLinkCue({
  info,
  kind,
  depColor,
  onAct,
  actLabel,
  editorial,
  cardBg,
  mutedColor,
}: {
  info: BrokenLinkCueInfo;
  kind: "archived" | "collapsed";
  depColor: string;
  onAct?: () => void;
  /** Verb label for the click action (e.g. "Restore" / "Expand"). */
  actLabel: string | null;
  editorial: boolean;
  cardBg: string;
  mutedColor: string;
}) {
  // Anchor at the vertical mid-line of the surviving card on the relevant side.
  const anchorX =
    info.side === "right" ? info.surviving.x + info.surviving.width : info.surviving.x;
  const anchorY = info.surviving.y + info.surviving.height / 2;
  // Small gap (6px) outside the card so the chip clears the card border.
  const gap = 6;
  const chipText = kind === "archived" ? "Linked task removed" : "Linked task hidden";
  const fullText = actLabel === null ? chipText : `${chipText} · ${actLabel}`;
  // Rough per-char width + padding; SVG <text> isn't clipped to the chip, so an
  // underestimate just makes the chip narrower than the text rather than cropping.
  const chipW = fullText.length * 6 + 16;
  const chipH = 18;
  // The chip sits OUTSIDE the card: align its inner edge to the anchor point,
  // then extend OUTWARD by chipW. (right: extends to the right; left: to the
  // left.)
  const chipX = info.side === "right" ? anchorX + gap : anchorX - gap - chipW;
  const chipY = anchorY - chipH / 2;
  const interactive = onAct !== undefined && actLabel !== null;
  return (
    <g
      data-testid="broken-link-cue"
      data-edge-id={info.edgeId}
      data-kind={kind}
      data-missing={info.missingId}
      data-side={info.side}
      style={{ pointerEvents: interactive ? "auto" : "none" }}
    >
      {interactive ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: an SVG <rect> is not keyboard-focusable; the equivalent restore/expand affordance is reachable from the drawer's "Comes after / Blocks" rows + the Archive panel (real <button>s), so no keyboard path is dropped.
        <rect
          data-testid="broken-link-cue-button"
          x={chipX}
          y={chipY}
          width={chipW}
          height={chipH}
          rx={5}
          // Calm amber on a paper backdrop. NEVER alarm-red.
          fill={editorial ? cardBg : "#FFFEF8"}
          stroke="#D97706"
          strokeOpacity={0.55}
          strokeWidth={1}
          style={{ cursor: "pointer" }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onAct?.();
          }}
        >
          <title>
            {kind === "archived" ? "Restore the linked task" : "Expand to reveal the linked task"}
          </title>
        </rect>
      ) : (
        <rect
          x={chipX}
          y={chipY}
          width={chipW}
          height={chipH}
          rx={5}
          fill={editorial ? cardBg : "#FFFEF8"}
          stroke="#D97706"
          strokeOpacity={0.4}
          strokeWidth={1}
        />
      )}
      <text
        data-testid="broken-link-cue-label"
        x={chipX + chipW / 2}
        y={anchorY}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fill={depColor}
        style={{ pointerEvents: "none" }}
      >
        {fullText}
      </text>
      {info.note.length > 0 && (
        <text
          data-testid="broken-link-cue-note"
          x={chipX + chipW / 2}
          y={anchorY + chipH / 2 + 8}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontStyle="italic"
          fill={mutedColor}
          style={{ pointerEvents: "none" }}
        >
          {info.note}
        </text>
      )}
    </g>
  );
}

export function DependencyArrows({
  positions,
  edges,
  offset,
  width,
  height,
  selectedEdgeId,
  onSelectEdge,
  layer = "visual",
  liveNodeIds,
  liveForestById,
  collapsedIds,
  onRestoreArchivedEndpoint,
  onExpandToReveal,
}: DependencyArrowsProps) {
  // A unique marker id per instance so two canvases (e.g. the split view)
  // don't collide on a shared `<marker id>`.
  const markerId = `dep-arrow-${useId()}`;
  const byId = new Map(positions.map((p) => [p.node.id, p]));
  const interactive = Boolean(onSelectEdge);
  // The interactive (hit-stroke) layer only exists when the edges are
  // interactive. When mounted as the interactive layer but no select handler is
  // wired (the read-only views never do this), there is nothing to draw.
  const isInteractiveLayer = layer === "interactive";
  // Editorial revamp (#1043, Wave 1): theme-aware stroke. Editorial = the warm
  // `--teal` accent + the `.t-edge.dep` dash (4 3) + an uppercase-mono label;
  // legacy = the gallery dependency hue (#0D9488) + dash (5 3) — byte-identical
  // when the team's `task_editorial_ui` flag is off. Rendered standalone (no
  // provider) ⇒ legacy default, so the existing arrow tests are unchanged.
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const depColor = editorial ? theme.palette.teal : DEP_COLOR;
  const depDash = editorial ? "4 3" : "5 3";

  // Round-4 ⑤.d + Bug A (#1043) — split the edge list into three groups:
  //   1. `drawable`        — both endpoints laid out; render normally.
  //   2. `archivedCues`    — exactly one endpoint laid out, the OTHER endpoint
  //                          is missing AND not in the live forest ⇒ the
  //                          partner was ARCHIVED. Render a calm broken-link
  //                          cue on the surviving endpoint; the lead can
  //                          Restore (when the handler is wired).
  //   3. `collapsedCues`   — exactly one endpoint laid out, the OTHER endpoint
  //                          IS in the live forest but not currently visible ⇒
  //                          a COLLAPSED ancestor hides it. The cue reads
  //                          "linked task hidden"; clicking un-collapses the
  //                          ancestor (when wired) so the edge reappears.
  // Backwards-compatible: with `liveNodeIds` omitted, group 2 + 3 stay empty
  // and every missing-endpoint edge falls through to the historic silent skip
  // (the existing tests are unchanged).
  const drawable: DependencyArrowEdge[] = [];
  const archivedCues: BrokenLinkCueInfo[] = [];
  const collapsedCues: BrokenLinkCueInfo[] = [];
  if (liveNodeIds) {
    for (const e of edges) {
      const fromIn = byId.has(e.from_node_id);
      const toIn = byId.has(e.to_node_id);
      if (fromIn && toIn) {
        drawable.push(e);
        continue;
      }
      // Exactly one endpoint is missing — classify by liveNodeIds.
      // (Both-missing edges keep the historic silent skip.)
      if (fromIn !== toIn) {
        const survivingId = fromIn ? e.from_node_id : e.to_node_id;
        const missingId = fromIn ? e.to_node_id : e.from_node_id;
        const surviving = byId.get(survivingId) as PositionedNode;
        // Side of the cue relative to the surviving card: the missing endpoint
        // "was" on the OTHER side of the edge. If the missing endpoint is the
        // `from`, the cue sits to the LEFT of the surviving card (the edge
        // would have entered from the left); if it is the `to`, it sits to the
        // RIGHT (the edge would have exited to the right).
        const side: "left" | "right" = fromIn ? "right" : "left";
        const info: BrokenLinkCueInfo = {
          edgeId: e.id,
          surviving,
          missingId,
          side,
          note: e.note?.trim() ?? "",
        };
        if (liveNodeIds.has(missingId)) {
          collapsedCues.push(info);
        } else {
          archivedCues.push(info);
        }
      }
    }
  } else {
    for (const e of edges) {
      if (byId.has(e.from_node_id) && byId.has(e.to_node_id)) drawable.push(e);
    }
  }

  // The INTERACTIVE layer is the only one that opts into pointer events, and it
  // only exists when a select handler is wired. Mounting an interactive layer
  // with no handler (never happens in practice) would draw nothing useful, so
  // we skip the whole SVG — keeps the DOM clean.
  if (isInteractiveLayer && !interactive) return null;

  return (
    <svg
      data-testid={isInteractiveLayer ? "dependency-arrows-hit" : "dependency-arrows"}
      // The SVG itself is always click-through. On the VISUAL layer nothing ever
      // opts back in (it stays inert, painting over the cards). On the
      // INTERACTIVE layer the per-edge hit-stroke groups opt IN to pointer
      // events — but that layer is mounted BELOW the cards (#1043), so a card
      // still wins the pointer wherever an edge merely crosses it; the
      // hit-stroke only catches clicks in the open space between cards.
      className="absolute left-0 top-0"
      style={{ pointerEvents: "none" }}
      width={width}
      height={height}
      aria-hidden
      role="presentation"
    >
      <title>{isInteractiveLayer ? "Task dependency hit targets" : "Task dependency arrows"}</title>
      {/* The arrowhead marker only belongs to the visual layer. */}
      {!isInteractiveLayer && (
        <defs>
          <marker id={markerId} markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
            <path d="M0,0 L0,6 L10,3 z" fill={depColor} />
          </marker>
        </defs>
      )}
      <g transform={`translate(${offset}, ${offset})`}>
        {drawable.map((edge) => {
          const from = byId.get(edge.from_node_id) as PositionedNode;
          const to = byId.get(edge.to_node_id) as PositionedNode;
          const { x1, y1, x2, y2 } = anchorPoints(from, to);
          // Route around any INTERVENING card so the link never disappears
          // behind it (#1043, Problem A). `routed` flags an edge whose chord
          // crossed an obstacle — exposed on the path for tests + so a crossing
          // edge can read as deliberately bowed. The SAME geometry feeds both
          // layers, so the hit-stroke traces the identical (possibly bowed) arc.
          const obstacles = obstaclesFor(positions, edge.from_node_id, edge.to_node_id);
          const routed = obstacles.some((o) => chordHitsBox(x1, y1, x2, y2, o));
          const cubic = arrowCubic(x1, y1, x2, y2, obstacles);
          const d = arrowPathFromCubic(cubic);
          const { x: mx, y: my } = arrowMidFromCubic(cubic);
          const isSelected = selectedEdgeId != null && edge.id === selectedEdgeId;
          const note = edge.note?.trim() ?? "";

          // ── The INTERACTIVE layer: ONLY the wide transparent hit-stroke ──────
          // Mounted BELOW the cards so a card wins the pointer where an edge
          // crosses it; edges stay selectable in the open space between cards.
          if (isInteractiveLayer) {
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents: an SVG <path> is not keyboard-focusable; this is a pointer-only convenience hit target for the arrow. The same edge is keyboard-reachable via the drawer's "Comes after / Blocks" rows (real buttons), so no canvas keyboard pairing is dropped.
              <path
                key={edge.id}
                data-testid="dependency-edge-hit"
                data-edge-id={edge.id}
                d={d}
                fill="none"
                stroke="transparent"
                // ~11px so the thin dashed line is comfortably clickable (#929).
                strokeWidth={11}
                style={{ pointerEvents: "auto", cursor: "pointer" }}
                onPointerDown={(e) => {
                  // Stop the canvas's background pan/clear from also firing.
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectEdge?.(edge.id);
                }}
              >
                <title>{`"${from.node.title}" comes before "${to.node.title}"`}</title>
              </path>
            );
          }

          // ── The VISUAL layer: halo + dashed stroke + arrowhead + note ────────
          // Always pointer-through; mounted ABOVE the cards so a crossing edge +
          // its label read clearly over/around them (the #1079 win, preserved).
          return (
            <g key={edge.id}>
              {/* Paper-coloured HALO beneath the dashed stroke (#1043, Problem
                  A). A solid, wider, paper-matching outline so the teal line
                  stays legible WHERE IT CROSSES a card — the edge reads clearly
                  over/around node cards instead of melting into them. Pure
                  visibility aid: pointer-through, drawn first so the dash sits
                  on top of it. */}
              <path
                data-testid="dependency-arrow-halo"
                d={d}
                fill="none"
                stroke={editorial ? theme.palette.canvas : "#FFFFFF"}
                strokeWidth={isSelected ? 6 : 4.5}
                strokeOpacity={0.9}
                strokeLinecap="round"
              />
              {/* The visible dashed arrow. Selected = thicker + opaque; otherwise
                  the calm B2.7 weight. `data-routed` marks an edge that bows
                  around an intervening card (not a straight chord). */}
              <path
                data-testid="dependency-arrow"
                data-from={edge.from_node_id}
                data-to={edge.to_node_id}
                data-selected={isSelected || undefined}
                data-routed={routed || undefined}
                d={d}
                fill="none"
                stroke={depColor}
                strokeWidth={isSelected ? 3 : 1.5}
                strokeOpacity={isSelected ? 1 : 0.85}
                strokeDasharray={depDash}
                markerEnd={`url(#${markerId})`}
              />
              {/* The note label (#929) — a small calm pill ON the edge so the
                  annotation reads without selecting. Factual, about the WORK
                  link (never a person). Pointer-through so it doesn't block the
                  edge hit target.

                  #1043, Problem A — labels NEVER truncate: the FULL note text
                  renders (no 24-char slice, no ellipsis, no fixed narrow
                  width, no clipping). The paper-coloured chip behind it is
                  sized to the full text so the label stays readable over the
                  dot-grid + any crossing arrow. */}
              {note.length > 0 &&
                (() => {
                  const shown = editorial ? note.toUpperCase() : note;
                  // Width the chip to the FULL text. Rough per-char estimate
                  // (≈6px/char) + horizontal padding; the SVG <text> isn't
                  // clipped to the chip, so even an underestimate never cuts the
                  // label off — the chip is a readability backdrop, not a clip.
                  const labelW = shown.length * 6 + 16;
                  return (
                    <g
                      data-testid="dependency-edge-note-label"
                      data-edge-id={edge.id}
                      style={{ pointerEvents: "none" }}
                      transform={`translate(${mx}, ${my})`}
                    >
                      <rect
                        data-testid="dependency-edge-note-chip"
                        x={-labelW / 2}
                        y={-9}
                        width={labelW}
                        height={18}
                        rx={5}
                        fill={editorial ? theme.palette.cardBg : "#FFFFFF"}
                        stroke={depColor}
                        strokeOpacity={0.4}
                      />
                      <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={editorial ? 9.5 : 10}
                        fontStyle={editorial ? "normal" : "italic"}
                        // Editorial edge labels are uppercase mono teal (the
                        // wireframe `EdgePath` label treatment).
                        fontFamily={editorial ? theme.fonts.mono : undefined}
                        letterSpacing={editorial ? "0.04em" : undefined}
                        fill={depColor}
                        // No `textLength`/clip → the full string always renders.
                        style={{ whiteSpace: "pre" }}
                      >
                        {shown}
                      </text>
                    </g>
                  );
                })()}
            </g>
          );
        })}
        {/* Round-4 ⑤.d + Bug A (#1043) — the broken-link cues. ONLY drawn on the
            VISUAL layer (the interactive hit-stroke layer has no cue to show
            because it has no arrow to anchor to here); painted AFTER the
            drawable arrows so the chips sit on top of any crossing edge for
            legibility. Each cue opts INTO pointer events ONLY when its
            click action is wired (the lead editor); otherwise the cue is a
            calm informational chip. */}
        {!isInteractiveLayer &&
          archivedCues.map((info) => {
            const onAct = onRestoreArchivedEndpoint
              ? () => onRestoreArchivedEndpoint(info.missingId)
              : null;
            return (
              <BrokenLinkCue
                key={`archived-${info.edgeId}`}
                info={info}
                kind="archived"
                depColor={depColor}
                actLabel={onAct ? "Restore" : null}
                {...(onAct ? { onAct } : {})}
                editorial={editorial}
                cardBg={theme.palette.cardBg}
                mutedColor={theme.palette.muted}
              />
            );
          })}
        {!isInteractiveLayer &&
          collapsedCues.map((info) => {
            // Resolve the collapsed ancestor on-click (#1043 Bug A). When the
            // parent didn't pass `liveForestById` + `collapsedIds`, the cue stays
            // informational — clicking still tells the user the link IS there,
            // hidden, even without a one-click expand.
            const ancestorId =
              liveForestById && collapsedIds
                ? findCollapsedAncestor(info.missingId, liveForestById, collapsedIds)
                : null;
            const onAct =
              onExpandToReveal && ancestorId !== null ? () => onExpandToReveal(ancestorId) : null;
            return (
              <BrokenLinkCue
                key={`collapsed-${info.edgeId}`}
                info={info}
                kind="collapsed"
                depColor={depColor}
                actLabel={onAct ? "Expand to see" : null}
                {...(onAct ? { onAct } : {})}
                editorial={editorial}
                cardBg={theme.palette.cardBg}
                mutedColor={theme.palette.muted}
              />
            );
          })}
      </g>
    </svg>
  );
}

/** Re-export the canvas palette teal alignment for any consumer wanting it. */
export const DEPENDENCY_ARROW_COLOR = DEP_COLOR;
// `PALETTE.teal` (#1F7A6E) is the brand teal for buttons/handles; the arrow
// teal (#0D9488) is the gallery's distinct dependency hue — referenced here
// so a future tweak finds both in one place.
export const LINK_HANDLE_COLOR = PALETTE.teal;
